import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "title is-5" }
const _hoisted_2 = { class: "contact-details__body__title" }

import { ContactAttributeViewModel } from '@/messaging/models/contactAttributeViewModel';
import { ContactViewModel } from '@/features/contacts/models/contactViewModel';
import { ContactAttributeType } from '@/messaging/models/contactAttributeType';
import EditableComponent from '@/shared/components/EditableComponent.vue';
import AttributeIconUtil from '@/shared/util/attributeIconUtil';
import Icon from '@/shared/icons/Icon.vue';
import appInsights from '@/shared/services/appInsights';
import WorkspaceViewModel from '@/workspaces/models/workspaceViewModel';
import TitledSection from '@/shared/components/TitledSection.vue';
import { computed, PropType, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useVModel } from '@vueuse/core';

type ContactAttributeValue = string | number | Date | undefined;

interface ContactAttributeRow {
  id: string;
  type: string;
  system: boolean;
  name: string;
  icon: string | undefined;
  value: ContactAttributeValue;
  options: string[];
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ContactDetails',
  props: {
  modelValue: {
    type: Object as PropType<ContactViewModel>,
    required: true,
  },
},
  emits: ["update:modelValue"],
  setup(__props, { emit: __emit }) {

const props = __props;

const store = useStore();
const currentWorkspace = computed<WorkspaceViewModel>(() => store.getters.currentWorkspace);
const contactAttributes = computed<ContactAttributeViewModel[]>(() => store.getters.contactAttributes);

const emit = __emit;
const contact = useVModel(props, 'modelValue', emit);
const data = ref<ContactAttributeRow[]>([]);

const columns = [
  { name: 'icon', display: '' },
  { name: 'name', display: 'Name' },
  { name: 'value', display: 'Value' },
];

watch(
  contact,
  () => {
    const attributes: ContactAttributeViewModel[] = contactAttributes.value.filter(
      (a: ContactAttributeViewModel) => a.id !== 'name',
    );

    attributes.unshift({
      id: 'type',
      name: 'Type',
      type: ContactAttributeType.Text,
      system: true,
    } as ContactAttributeViewModel);
    attributes.unshift({
      id: 'surname',
      name: 'Last Name',
      type: ContactAttributeType.Text,
      system: true,
    } as ContactAttributeViewModel);
    attributes.unshift({
      id: 'givenName',
      name: 'First Name',
      type: ContactAttributeType.Text,
      system: true,
    } as ContactAttributeViewModel);

    data.value = [];
    attributes.forEach((a) =>
      data.value.push({
        id: a.id,
        type: getHtmlInputTypeForAttribute(a),
        system: a.system,
        name: a.name,
        icon: AttributeIconUtil.getIconType(a.type, a.id, a.system),
        value: getAttributeDisplayValue(a),
        options: a.name === 'Type' ? ['Lead', 'User'] : [],
      } as ContactAttributeRow),
    );
  },
  { immediate: true, deep: true },
);

function contactUpdated(row: ContactAttributeRow, value: any) {
  appInsights.trackEvent(
    { name: 'Contact details updated' },
    { workspaceId: currentWorkspace.value.id, field: row.id, value },
  );
  if (row.id === 'givenName') {
    contact.value.givenName = value;
  } else if (row.id === 'surname') {
    contact.value.surname = value;
  } else if (row.id === 'type') {
    contact.value.type = value.toLowerCase();
  } else if (row.id === 'email') {
    contact.value.email = value;
  } else if (row.id === 'phoneNumber') {
    contact.value.phoneNumber = value;
  } else if (row.id === 'firstSeen') {
    contact.value.firstSeenUtc = value;
  } else if (row.id === 'lastSeen') {
    contact.value.lastSeenUtc = value;
  } else if (row.id === 'lastContacted') {
    contact.value.lastContactedUtc = value;
  } else if (row.id === 'lastHeardFrom') {
    contact.value.lastHeardFromUtc = value;
  } else {
    contact.value.customAttributes[row.id] = value;
  }

  contact.value = { ...contact.value };
}

function getHtmlInputTypeForAttribute(attribute: ContactAttributeViewModel) {
  if (attribute.type === 'date' && attribute.system) {
    return 'datetime-local';
  }
  if (attribute.id === 'email') {
    return 'email';
  }

  if (attribute.id === 'phoneNumber') {
    return 'tel';
  }

  if (attribute.id === 'type') {
    return 'select';
  }
  return attribute.type;
}

function getAttributeDisplayValue(attribute: ContactAttributeViewModel): any {
  if (attribute.system) {
    return getSystemAttributeDisplayValue(attribute);
  } else {
    return getCustomAttributeDisplayValue(attribute);
  }
}

function getSystemAttributeDisplayValue(attribute: ContactAttributeViewModel) {
  if (attribute.type === ContactAttributeType.Date) {
    const value = (contact.value as any)[attribute.id + 'Utc'];
    if (value) {
      return new Date(value);
    }
    return value;
  } else {
    const value = (contact.value as any)[attribute.id];
    if (attribute.id === 'type') {
      return value.charAt(0).toUpperCase() + value.slice(1);
    }
    return value;
  }
}

function getCustomAttributeDisplayValue(attribute: ContactAttributeViewModel) {
  const value = contact.value.customAttributes[attribute.id];
  if (attribute.type === ContactAttributeType.Date && value) {
    return new Date(value);
  }
  return value;
}

return (_ctx: any,_cache: any) => {
  const _component_n_avatar = _resolveComponent("n-avatar")!
  const _component_n_data_table = _resolveComponent("n-data-table")!

  return (_openBlock(), _createBlock(TitledSection, {
    class: "contact-details",
    "align-content-left": true,
    "icon-colour": "inherit"
  }, {
    icon: _withCtx(() => [
      _createVNode(_component_n_avatar, {
        name: _unref(contact).name,
        imageUrl: _unref(contact).avatarUrl
      }, null, 8, ["name", "imageUrl"])
    ]),
    title: _withCtx(() => [
      _createElementVNode("h2", _hoisted_1, _toDisplayString(_unref(contact).name || 'Unknown'), 1)
    ]),
    default: _withCtx(() => [
      _createVNode(_component_n_data_table, {
        class: "contact-details__body",
        columns: columns,
        items: data.value
      }, {
        icon: _withCtx(({ row }) => [
          _createVNode(Icon, {
            icon: row.icon
          }, null, 8, ["icon"])
        ]),
        name: _withCtx(({ row }) => [
          _createElementVNode("span", _hoisted_2, _toDisplayString(row.name), 1)
        ]),
        value: _withCtx(({ row }) => [
          _createVNode(EditableComponent, {
            modelValue: row.value,
            "onUpdate:modelValue": [($event: any) => ((row.value) = $event), (edited) => contactUpdated(row, edited)],
            type: row.type,
            options: row.options,
            "field-name": row.name
          }, null, 8, ["modelValue", "onUpdate:modelValue", "type", "options", "field-name"])
        ]),
        _: 1
      }, 8, ["items"])
    ]),
    _: 1
  }))
}
}

})